import { useEffect, useState } from "react"

// ** Third Party Libraries
import { z } from "zod"
import { Loader } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery } from "@tanstack/react-query"
import { CloseCircle } from "iconsax-reactjs"

// ** Theme Components
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@pma/components/common/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@pma/components/common/select"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Types
import { DictType } from "@pma/types"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"
import { getUserRoleApi, inviteUserService } from "@pma/services/common"
import Recaptcha from "@pma/components/auth/recaptcha"

const inviteUserSchema = z.object({
  userName: z
    .string()
    .min(1, { message: "User name is required" })
    .min(2, { message: "User name must be at least 2 characters" })
    .max(50, { message: "User name must be less than 50 characters" }),
  email: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Please enter a valid email address" }),
  role: z.string().min(1, { message: "Please select a role" }),
})

type InviteUserFormValues = z.infer<typeof inviteUserSchema>

interface InviteUserDialogProps {
  setInviteUserDialog: (value: boolean) => void
  inviteUserDialog: boolean
  defaultValues?: {
    userName: string
    email: string
    role: string
  }
}

const InviteUserDialog = ({ inviteUserDialog, setInviteUserDialog, defaultValues }: InviteUserDialogProps) => {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()
  
  const isHebrew = locale === "he"
  const orgId = authConfig?.last_organization_id
  const currentPath = typeof window !== "undefined" ? window.location.href : ""

  const [v2Fallback, setV2Fallback] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<DictType>({ v2: "", v3: "" })

  const form = useForm<InviteUserFormValues>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: {
      role: "",
      email: "",
      userName: "",
    },
  })

  useEffect(() => {
    if (inviteUserDialog) {
      form.reset({
        userName: defaultValues?.userName || "",
        email: defaultValues?.email || "",
        role: defaultValues?.role || "",
      })
    }
  }, [inviteUserDialog, defaultValues, form])

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form

  const { data } = useQuery({
    queryKey: ["roles", orgId],
    queryFn: () => getUserRoleApi({ context_type: "organization user", context_id: orgId }),
  })

  const onSubmit = (formData: InviteUserFormValues) => {
    sendEmailMutation.mutate({
      user_name: formData.userName,
      email: formData.email,
      user_role_id: formData.role,
      organization_id: orgId,
    })
  }

  const reSubmit = (token: string) => location.reload()

  const sendResetLink = async (payload: DictType) => {
    if (!v2Fallback && !recaptchaToken.v3) {
      return toast({
        title: translate("Info"),
        description: translate("Please complete the reCAPTCHA"),
        variant: "info",
      })
    }

    if ((v2Fallback && !payload?.v2Token) || !recaptchaToken.v3) {
      return
    }

    const isLocalHost = process.env.NODE_ENV === "development"
    const isDev = currentPath.includes("https://rog-tech.io/dev.pms")
    const originEnvironment = isDev
      ? { for_dev: true, for_test: false }
      : isLocalHost
        ? { for_test: true }
        : { for_test: false, for_dev: false }

    const finalResponse = {
      ...payload,
      ...originEnvironment,
      recaptcha_response: !v2Fallback ? recaptchaToken.v3 : undefined,
      recaptcha_response_v2: payload?.v2Token,
    }

    return await inviteUserService(finalResponse)
  }

  const sendEmailMutation = useMutation({
    mutationKey: ["invite-user"],
    mutationFn: sendResetLink,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Invite sent successfully"),
        variant: "success",
      })
      setInviteUserDialog(false)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      if (errorMessage === "Invalid Captcha Response. Please try again.") {
        setV2Fallback(true)
      } else {
        toast({
          title: translate("Error"),
          description: translate(errorMessage || "Unable to send invite"),
          variant: "error",
        })
      }
    },
  })

  if (!inviteUserDialog) return null

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm'
      onClick={() => setInviteUserDialog(false)}
    >
      <div
        className='relative w-full max-w-md rounded-2xl bg-white shadow-xl'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className='flex items-center justify-between border-b border-neutral-200 px-5 py-4'>
          <h2 className='font-[inter] text-base font-medium text-neutral-800'>
            {translate("Invite User")}
          </h2>
          <CloseCircle
            className='cursor-pointer text-neutral-800'
            size={24}
            onClick={() => setInviteUserDialog(false)}
          />
        </div>

        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div
              className='flex flex-col gap-3 overflow-y-auto border-b p-6'
              style={{ maxHeight: "calc(100vh - 220px)" }}
            >
              {/* Full Name */}
              <FormField
                control={control}
                name='userName'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {translate("Full Name")} <small className='text-red-500'>*</small>
                    </FormLabel>
                    <FormControl>
                      <input
                        {...field}
                        className={cn(
                          "w-full rounded-lg border px-3 py-2 text-sm outline-none",
                          errors?.userName
                            ? "border-error-300 focus:border-error-500 focus:ring-2 focus:ring-error-500"
                            : "border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
                        )}
                        placeholder={translate("Enter full name")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Email */}
              <FormField
                control={control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {translate("Email")} <small className='text-red-500'>*</small>
                    </FormLabel>
                    <FormControl>
                      <input
                        {...field}
                        type='email'
                        className={cn(
                          "w-full rounded-lg border px-3 py-2 text-sm outline-none",
                          errors?.email
                            ? "border-error-300 focus:border-error-500 focus:ring-2 focus:ring-error-500"
                            : "border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500",
                        )}
                        placeholder={translate("Enter email address")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Role */}
              <FormField
                control={control}
                name='role'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {translate("Role")} <small className='text-red-500'>*</small>
                    </FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger
                          className={cn(
                            isHebrew ? "rtl" : "ltr",
                            errors?.role ? "border-error-300 focus:ring-error-500" : "",
                          )}
                        >
                          <SelectValue placeholder={translate("Select Role")} />
                        </SelectTrigger>
                        <SelectContent className={cn('rounded-xl', isHebrew ? "rtl" : "ltr")}>
                          {data?.map((role: any, index: number) => (
                            <SelectItem value={String(role.id)} className='rounded-lg' key={index}>
                              {translate(role.name)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Footer */}
            <div className='flex items-center justify-end gap-3 p-6'>
              <button
                className='rounded-full border border-neutral-200 px-6 py-2 font-[inter] text-sm font-normal text-neutral-800'
                type='button'
                onClick={() => setInviteUserDialog(false)}
              >
                {translate("Cancel")}
              </button>
              <button
                className='flex rounded-full px-6 py-2 font-[inter] text-sm font-medium text-white shadow'
                type='submit'
                disabled={sendEmailMutation.isPending}
                style={{
                  background:
                    "linear-gradient(0deg, var(--Indigo-8, #444CE7), var(--Indigo-8, #444CE7)), linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%)",
                }}
              >
                {sendEmailMutation.isPending ? (
                  <Loader className={cn("animate-spin", isHebrew ? "ml-1" : "mr-1")} size={16} />
                ) : null}{" "}
                {translate("Send Invite")}
              </button>
            </div>
          </form>
        </Form>

        <Recaptcha setToken={setRecaptchaToken} v2={v2Fallback} reSubmit={reSubmit} />
      </div>
    </div>
  )
}

export default InviteUserDialog
